import React from "react";
import { ScrollText, UserCheck, FileText, Shield, AlertTriangle, Check, BookOpen, BarChart, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Terms() {
  return (
    <>
      <section className="bg-muted/30 pt-32 pb-12">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex flex-col items-center">
              <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-yellow-500 to-amber-400 bg-clip-text text-transparent mb-3">
                Terms of Service
              </h1>
              <div className="w-48 h-1 bg-gradient-to-r from-yellow-500 to-amber-400 rounded-full"></div>
            </div>
            <p className="text-xl text-muted-foreground">
              Last Updated: July 26, 2024
            </p>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container">
          <div className="max-w-4xl mx-auto prose prose-slate">
            <div className="space-y-10">
              {/* Introduction Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <ScrollText className="h-6 w-6 text-yellow-500" />
                  Introduction
                </h2>
                <p className="text-muted-foreground">
                  Welcome to Pagent Social Corp. These Terms of Service govern your use of our website, 
                  applications, content, products and services ("Services"). By accessing or using our Services, 
                  you agree to be bound by these terms and all applicable laws and regulations.
                </p>
                <p className="text-muted-foreground mt-4">
                  If you do not agree with any part of these terms, you may not access or use our Services.
                </p>
              </div>

              {/* Acceptance of Terms */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Check className="h-6 w-6 text-yellow-500" />
                  1. Acceptance of Terms
                </h2>
                <p className="text-muted-foreground">
                  By accessing and using Pagent's services, including our mobile application and website, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
                <p className="text-muted-foreground mt-4">
                  We may update and change these Terms from time to time. If we make material changes, we will notify you via the Services or by other means to provide you with the opportunity to review the changes before they become effective. Your continued use of our Services after any changes indicates your acceptance of the updated Terms.
                </p>
              </div>

              {/* Eligibility Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <UserCheck className="h-6 w-6 text-yellow-500" />
                  2. Eligibility
                </h2>
                <p className="text-muted-foreground">
                  You must be at least 13 years old to use our services. If you are under 18, you must have parental consent to use our platform. By using our services, you represent that you meet these eligibility requirements.
                </p>
                <p className="text-muted-foreground mt-4">
                  We reserve the right to terminate accounts of users who misrepresent their age or identity, or otherwise do not meet our eligibility requirements.
                </p>
              </div>

              {/* User Accounts Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <FileText className="h-6 w-6 text-yellow-500" />
                  3. User Accounts
                </h2>
                <p className="text-muted-foreground">
                  To participate in competitions and access certain features, you must create an account. You are responsible for:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Providing accurate and up-to-date information</li>
                  <li>Promptly updating your information if it changes</li>
                  <li>Ensuring your account is not shared with or accessed by others</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  We reserve the right to disable any user account if, in our opinion, you have violated any provision of these Terms.
                </p>
              </div>

              {/* Competition Rules Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <BarChart className="h-6 w-6 text-yellow-500" />
                  4. Competition Rules
                </h2>
                <p className="text-muted-foreground">
                  When participating in competitions on our platform:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Follow all competition-specific rules and guidelines</li>
                  <li>Submit only original content that you have the rights to use</li>
                  <li>Respect other participants and maintain appropriate conduct</li>
                  <li>Accept community voting results as final</li>
                  <li>Understand that prizes are subject to availability and may change without notice</li>
                  <li>Recognize that all decisions regarding competition outcomes are at our sole discretion</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Competitions may have additional terms and conditions, which will be provided at the time of the competition. These additional terms are incorporated into these Terms of Service.
                </p>
              </div>

              {/* Content Guidelines Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Shield className="h-6 w-6 text-yellow-500" />
                  5. Content Guidelines
                </h2>
                <p className="text-muted-foreground">
                  Users must not post content that is:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Illegal or promotes illegal activities</li>
                  <li>Harassing, abusive, or threatening</li>
                  <li>Infringing on intellectual property rights</li>
                  <li>Sexually explicit or inappropriate</li>
                  <li>Promoting discrimination or hatred</li>
                  <li>False, deceptive, or misleading</li>
                  <li>Violating others' privacy or confidentiality</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  We reserve the right to remove any content that violates these guidelines or is otherwise objectionable, without prior notice. Repeated violations may result in account termination.
                </p>
              </div>

              {/* Termination Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <AlertTriangle className="h-6 w-6 text-yellow-500" />
                  6. Termination
                </h2>
                <p className="text-muted-foreground">
                  We reserve the right to terminate or suspend accounts that violate these terms or engage in inappropriate behavior on our platform. Users may also delete their accounts at any time.
                </p>
                <p className="text-muted-foreground mt-4">
                  Upon termination, your right to use the Services will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>
              </div>

              {/* Changes to Terms Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <BookOpen className="h-6 w-6 text-yellow-500" />
                  7. Changes to Terms
                </h2>
                <p className="text-muted-foreground">
                  We may modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.
                </p>
                <p className="text-muted-foreground mt-4">
                  If you do not agree to the modified terms, you should discontinue your use of the Services. We will make reasonable efforts to notify you of any material changes to these Terms.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Shield className="h-6 w-6 text-yellow-500" />
                  8. Intellectual Property
                </h2>
                <p className="text-muted-foreground">
                  All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, and software, are owned by Pagent Social Corp., its licensors, or other providers and are protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-muted-foreground mt-4">
                  You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services without our prior written consent.
                </p>
              </div>

              {/* Contact Section */}
              <div className="bg-card rounded-lg p-8 shadow-sm border">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Mail className="h-6 w-6 text-yellow-500" />
                  Contact Us
                </h2>
                <p className="text-muted-foreground mb-6">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="outline" className="gap-2">
                    <Mail className="h-4 w-4 text-yellow-500" />
                    terms@pagent.com
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <ScrollText className="h-4 w-4 text-yellow-500" />
                    Visit Help Center
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">
                  Last updated: July 26, 2024
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
} 